import { COLORS } from "../core/constants.ts";
import { PANEL } from "../rendering/designTokens.ts";
import { UI_FONT_STACK, UI_Z, roleFontSize } from "./uiTokens.ts";
import { getStageFit } from "../utils/stage.ts";

/**
 * InfoTooltip (CR-1) — ONE shared, card-style hover tooltip reused by every HUD
 * surface that needs to explain a thing: the skill deck, the tower selector, the
 * payload rack (CR-1), the MULT readout (CR-5), and the inline sell confirm look
 * (CR-4). Structured like the draft cards — name → impact → body → meta — but
 * WITHOUT the art/graphic element, so it reads at a glance.
 *
 * Pure DOM/CSS (render-only, determinism-neutral). `pointer-events:none` so it
 * never blocks a build/deploy click; auto-removed on hide; clamped on-screen
 * (far-right panels flip/clamp left) so it can't clip off the edge.
 */
export interface InfoTooltipModel {
  /** Headline name (rendered in the accent colour). */
  name: string;
  /** Rarity/element accent colour for the name + left rule. */
  accent: string;
  /** Optional right-aligned header chip (rarity / role / "PAYLOAD · ◎AIM"). */
  tag?: string | undefined;
  /** The IMPACT line — the prominent "what it does" (headline / DMG·DPS·…). */
  impact?: string | undefined;
  /** Body lines (description / special / synergy) — dimmer, the what/why. */
  body?: string[] | undefined;
  /** Footer/meta line (cost / "[rarity] · N waves"). */
  meta?: string | undefined;
  /** Payload "how to deploy" line (auto vs ◎aim-enemy vs ◎aim-cell). */
  how?: string | undefined;
}

export type InfoTooltipPlacement = "above" | "left";

const STYLE_ID = "info-tooltip-styles";

export class InfoTooltip {
  private el: HTMLDivElement | null = null;

  constructor() {
    InfoTooltip.ensureStyles();
  }

  /** Show the tooltip anchored to `anchor`. Replaces any visible tooltip. */
  show(
    anchor: HTMLElement,
    model: InfoTooltipModel,
    placement: InfoTooltipPlacement = "above",
  ): void {
    this.hide();
    const tip = document.createElement("div");
    tip.className = "info-tooltip";
    tip.style.setProperty("--it-accent", model.accent);

    const parts: string[] = [];
    parts.push(
      `<div class="it-head"><span class="it-name">${model.name}</span>${
        model.tag ? `<span class="it-tag">${model.tag}</span>` : ""
      }</div>`,
    );
    if (model.impact) parts.push(`<div class="it-impact">${model.impact}</div>`);
    for (const line of model.body ?? []) {
      if (line) parts.push(`<div class="it-body">${line}</div>`);
    }
    if (model.how) parts.push(`<div class="it-how">${model.how}</div>`);
    if (model.meta) parts.push(`<div class="it-meta">${model.meta}</div>`);
    tip.innerHTML = parts.join("");

    // Mount inside the fixed-stage container so the tooltip scales with the
    // game; position() bridges viewport → stage-local coords. Falls back to
    // <body> in headless/test DOM (where getStageFit() is an identity fit).
    (document.getElementById("game-container") ?? document.body).appendChild(
      tip,
    );
    this.el = tip;
    this.position(anchor, tip, placement);
  }

  hide(): void {
    if (this.el) {
      this.el.remove();
      this.el = null;
    }
  }

  /** Anchor + clamp into the viewport so it never clips off-screen. */
  private position(
    anchor: HTMLElement,
    tip: HTMLElement,
    placement: InfoTooltipPlacement,
  ): void {
    // The tooltip lives inside the transformed stage, so its style.left/top are
    // in stage-local design space — but getBoundingClientRect() returns viewport
    // (post-transform) coords. Convert anchor rect + tip size into design space
    // via the live stage fit, and clamp to the design bounds. With no stage
    // (headless/test) the fit is identity, so this is the original viewport math.
    const fit = getStageFit();
    const s = fit.scale;
    const ar = anchor.getBoundingClientRect();
    const tr = tip.getBoundingClientRect();
    const aLeft = (ar.left - fit.offsetX) / s;
    const aTop = (ar.top - fit.offsetY) / s;
    const aRight = (ar.right - fit.offsetX) / s;
    const aBottom = (ar.bottom - fit.offsetY) / s;
    const tw = tr.width / s;
    const th = tr.height / s;
    const vw = fit.designW || window.innerWidth || 1280;
    const vh = fit.designH || window.innerHeight || 720;
    const pad = 8;
    let left: number;
    let top: number;

    if (placement === "left") {
      // Far-right panels (rack / loadout / deploy): sit to the LEFT of the
      // anchor; fall back to the right only if there's no room left.
      left = aLeft - tw - pad;
      if (left < pad) left = aRight + pad;
      top = aTop;
    } else {
      // Default: above the anchor, left-aligned; drop below if it'd clip the top.
      left = aLeft;
      top = aTop - th - pad;
      if (top < pad) top = aBottom + pad;
    }

    // Clamp into the stage on both axes (no off-screen clip).
    left = Math.max(pad, Math.min(left, vw - tw - pad));
    top = Math.max(pad, Math.min(top, vh - th - pad));
    tip.style.left = `${left}px`;
    tip.style.top = `${top}px`;
  }

  private static ensureStyles(): void {
    if (typeof document === "undefined" || document.getElementById(STYLE_ID)) {
      return;
    }
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      .info-tooltip {
        position: fixed; z-index: ${UI_Z.TOOLTIP}; pointer-events: none;
        width: 210px; box-sizing: border-box;
        padding: 8px 10px 9px;
        font-family: ${UI_FONT_STACK};
        background: ${PANEL.BG};
        border: 1px solid color-mix(in srgb, var(--it-accent, ${COLORS.SENTINEL_GREEN}) 55%, transparent);
        border-left: 3px solid var(--it-accent, ${COLORS.SENTINEL_GREEN});
        border-radius: 5px;
        box-shadow: 0 6px 20px -6px #000,
          0 0 14px color-mix(in srgb, var(--it-accent, ${COLORS.SENTINEL_GREEN}) 18%, transparent);
        color: ${COLORS.TEXT_PRIMARY}; line-height: 1.45;
        /* D-262 readability fix: long copy (worst case the DE compounds) wraps
           cleanly INSIDE the 210px box. overflow-wrap only breaks a token that
           cannot fit on its own line — normal words still wrap at spaces, so no
           gratuitous mid-word breaks and no spill over the tower window. */
        overflow-wrap: break-word; word-break: normal;
      }
      /* BL-203 — the headline must WRAP inside the box. A flex row whose children
         can't shrink overflows the fixed 210px box even with the container's
         overflow-wrap (the role tag "SUPPORT / Debuff (no direct damage)" is the
         worst case, longer still in DE). flex-wrap lets a long tag drop to the
         next line; min-width:0 + overflow-wrap on each child lets the name/tag
         themselves wrap rather than push past the edge. No white-space:nowrap. */
      .info-tooltip .it-head {
        display: flex; align-items: baseline; justify-content: space-between;
        flex-wrap: wrap; gap: 2px 8px;
        border-bottom: 1px solid color-mix(in srgb, var(--it-accent, ${COLORS.SENTINEL_GREEN}) 22%, transparent);
        padding-bottom: 4px; margin-bottom: 5px;
      }
      .info-tooltip .it-name {
        color: var(--it-accent, ${COLORS.SENTINEL_GREEN}); font-weight: bold;
        font-size: ${roleFontSize("label")}; letter-spacing: 0.04em;
        min-width: 0; overflow-wrap: break-word; word-break: normal;
      }
      .info-tooltip .it-tag {
        flex: 0 1 auto; min-width: 0; color: #8aa0a8; font-size: ${roleFontSize("micro")};
        letter-spacing: 0.1em; text-transform: uppercase; opacity: 0.85;
        overflow-wrap: break-word; word-break: normal;
      }
      .info-tooltip .it-impact {
        color: ${COLORS.SENTINEL_GREEN}; font-size: ${roleFontSize("label")}; font-weight: bold;
        letter-spacing: 0.02em; margin-bottom: 4px;
        text-shadow: 0 0 8px rgba(136, 224, 208, 0.25);
      }
      .info-tooltip .it-body {
        color: #9fb6a6; font-size: ${roleFontSize("caption")}; margin-top: 2px;
      }
      .info-tooltip .it-how {
        color: ${COLORS.ICE_CYAN}; font-size: ${roleFontSize("caption")}; margin-top: 5px;
        letter-spacing: 0.02em;
      }
      .info-tooltip .it-meta {
        color: #6f8a72; font-size: ${roleFontSize("micro")}; letter-spacing: 0.06em;
        margin-top: 6px; padding-top: 4px;
        border-top: 1px solid color-mix(in srgb, var(--it-accent, ${COLORS.SENTINEL_GREEN}) 14%, transparent);
      }
    `;
    document.head.appendChild(style);
  }
}
